import * as React from 'react';
import {
    Container, Box, Paper, TextField, InputAdornment, Button,
    Stack, IconButton, CircularProgress, Typography, Pagination,
    Collapse, Chip, Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import { pagosProveedor } from '../Componentes/FunctionLogin.js';
import { useTranslation } from 'react-i18next';
import PaymentCard from './PaymentCard';
import EmptyState from './EmptyState';

export default function PagosList() {
    const [t] = useTranslation("global");

    const [searchTerm, setSearchTerm] = React.useState('');
    const [payments, setPayments] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [showFilters, setShowFilters] = React.useState(false);
    const [fechaDesde, setFechaDesde] = React.useState('');
    const [fechaHasta, setFechaHasta] = React.useState('');
    const itemsPerPage = 10;

    const loadPayments = async () => {
        setLoading(true);
        try {
            const result = await pagosProveedor();
            setPayments(result);
        } catch (error) {
            console.error('Error loading payments:', error);
            setPayments([]);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        loadPayments();
    }, []);

    React.useEffect(() => {
        setPage(1);
    }, [searchTerm, fechaDesde, fechaHasta]);

    const hasDateFilter = fechaDesde || fechaHasta;

    const clearDateFilters = () => {
        setFechaDesde('');
        setFechaHasta('');
    };

    const filteredPayments = React.useMemo(() => {
        let result = payments;

        // 1. Filtro por texto
        const searchLower = searchTerm.toLowerCase().trim();
        if (searchLower) {
            result = result.filter(payment => {
                const idStr = String(payment.avisoPago || '').toLowerCase();
                const currStr = String(payment.monedaPago || '').toLowerCase();

                if (idStr.includes(searchLower)) return true;
                if (currStr.includes(searchLower)) return true;

                const matchInvoice = (payment.facturasPagadas || []).some(inv => {
                    const refExterna = String(inv.referenciaFactura || '').toLowerCase();
                    const docOriginal = String(inv.idDocumentoOriginal || '').toLowerCase();
                    return refExterna.includes(searchLower) || docOriginal.includes(searchLower);
                });

                return matchInvoice;
            });
        }

        // 2. Filtro por fechas
        if (fechaDesde) {
            result = result.filter(p => {
                if (!p.fechaAviso) return false;
                const fecha = new Date(p.fechaAviso);
                return fecha >= new Date(fechaDesde);
            });
        }
        if (fechaHasta) {
            result = result.filter(p => {
                if (!p.fechaAviso) return false;
                const fecha = new Date(p.fechaAviso);
                // Include full day of fechaHasta
                const hasta = new Date(fechaHasta);
                hasta.setHours(23, 59, 59, 999);
                return fecha <= hasta;
            });
        }

        // 3. Ordenar del más reciente al más antiguo
        result = [...result].sort((a, b) => {
            const dateA = a.fechaAviso ? new Date(a.fechaAviso).getTime() : 0;
            const dateB = b.fechaAviso ? new Date(b.fechaAviso).getTime() : 0;
            return dateB - dateA;
        });

        return result;
    }, [payments, searchTerm, fechaDesde, fechaHasta]);

    const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
    const paginatedPayments = filteredPayments.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: '800', color: '#132C4A' }}>
                {t("pagos.titulo") || "Pagos Realizados"}
            </Typography>

            {/* Barra de búsqueda + botón filtros */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    border: '1px solid #E5E7EB',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
                }}
            >
                {/* Fila principal */}
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <TextField
                            placeholder={t("pagos.buscar") || "Buscar aviso, factura o referencia..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ flexGrow: 1, minWidth: '250px' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#9CA3AF' }} />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2 }
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={<SearchIcon />}
                            onClick={() => setPage(1)}
                            sx={{
                                bgcolor: '#BD1F34',
                                '&:hover': { bgcolor: '#9a192a' },
                                height: '56px',
                                px: 4,
                                borderRadius: 2,
                                fontWeight: 600
                            }}
                        >
                            {t("pagos.boton_buscar") || "Buscar"}
                        </Button>

                        {/* Botón de filtros de fecha */}
                        <Button
                            variant={showFilters ? "contained" : "outlined"}
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(prev => !prev)}
                            sx={{
                                height: '56px',
                                px: 3,
                                borderRadius: 2,
                                fontWeight: 600,
                                ...(showFilters
                                    ? {
                                        bgcolor: '#132C4A',
                                        '&:hover': { bgcolor: '#0e2038' },
                                        color: '#fff',
                                        border: 'none'
                                    }
                                    : {
                                        borderColor: '#D1D5DB',
                                        color: '#374151',
                                        '&:hover': { borderColor: '#BD1F34', color: '#BD1F34', bgcolor: 'rgba(189,31,52,0.05)' }
                                    }
                                )
                            }}
                        >
                            {t("pagos.filtrar_fecha") || "Filtrar por fecha"}
                            {hasDateFilter && (
                                <Chip
                                    label="●"
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        height: 16,
                                        fontSize: '0.6rem',
                                        bgcolor: '#22C55E',
                                        color: '#fff',
                                        '& .MuiChip-label': { px: 0.8 }
                                    }}
                                />
                            )}
                        </Button>

                        <IconButton
                            onClick={() => {
                                setSearchTerm('');
                                clearDateFilters();
                                loadPayments();
                            }}
                            sx={{
                                backgroundColor: '#F3F4F6',
                                height: '56px',
                                width: '56px',
                                borderRadius: 2,
                                '&:hover': { backgroundColor: '#E5E7EB' }
                            }}
                            title={t("pagos.actualizar") || "Actualizar"}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Stack>
                </Box>

                {/* Panel de filtros de fecha (desplegable) */}
                <Collapse in={showFilters}>
                    <Divider />
                    <Box
                        sx={{
                            px: 3,
                            py: 2.5,
                            background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)',
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                        >
                            {/* Icono decorativo */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#132C4A' }}>
                                <CalendarTodayIcon sx={{ fontSize: 20 }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#132C4A' }}>
                                    {t("pagos.rango_fechas") || "Rango de fechas"}
                                </Typography>
                            </Box>

                            <TextField
                                label={t("pagos.fecha_desde") || "Fecha desde"}
                                type="date"
                                value={fechaDesde}
                                onChange={(e) => setFechaDesde(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: fechaHasta || undefined }}
                                size="small"
                                sx={{
                                    minWidth: 180,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: '#fff',
                                        '&.Mui-focused fieldset': { borderColor: '#BD1F34' }
                                    },
                                    '& label.Mui-focused': { color: '#BD1F34' }
                                }}
                            />

                            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', color: '#9CA3AF' }}>
                                <Typography variant="body2">—</Typography>
                            </Box>

                            <TextField
                                label={t("pagos.fecha_hasta") || "Fecha hasta"}
                                type="date"
                                value={fechaHasta}
                                onChange={(e) => setFechaHasta(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: fechaDesde || undefined }}
                                size="small"
                                sx={{
                                    minWidth: 180,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: '#fff',
                                        '&.Mui-focused fieldset': { borderColor: '#BD1F34' }
                                    },
                                    '& label.Mui-focused': { color: '#BD1F34' }
                                }}
                            />

                            {/* Chip resumen del filtro activo */}
                            {hasDateFilter && (
                                <Chip
                                    label={`${fechaDesde || '…'} → ${fechaHasta || '…'}`}
                                    onDelete={clearDateFilters}
                                    deleteIcon={<CloseIcon />}
                                    sx={{
                                        bgcolor: 'rgba(189,31,52,0.1)',
                                        color: '#BD1F34',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        '& .MuiChip-deleteIcon': { color: '#BD1F34' }
                                    }}
                                />
                            )}
                        </Stack>
                    </Box>
                </Collapse>
            </Paper>

            {/* Content */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={60} sx={{ color: '#BD1F34' }} />
                </Box>
            ) : paginatedPayments.length > 0 ? (
                <Box>
                    <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
                        {t("pagos.mostrando", { count: filteredPayments.length, total: payments.length }) || `Mostrando ${filteredPayments.length} de ${payments.length} registros`}
                    </Typography>

                    <Stack spacing={2}>
                        {paginatedPayments.map((payment, index) => (
                            <PaymentCard key={payment.id || index} payment={payment} />
                        ))}
                    </Stack>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}
                </Box>
            ) : (
                <EmptyState
                    type="no-results"
                    message={searchTerm || hasDateFilter
                        ? (t("pagos.no_resultados") || "No se encontraron pagos para esta búsqueda")
                        : (t("pagos.no_resultados") || "No se encontraron pagos")
                    }
                    onClear={() => { setSearchTerm(''); clearDateFilters(); }}
                />
            )}
        </Container>
    );
}
